import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { EntityHeader } from "../entity-header";
import { EntityRemovedPanel } from "../entity-removed";
import { DateTime } from "luxon";
import {
  ShareableEntity,
  useSwapPresignedUrl,
  useAddSwapImage,
} from "@/lib/api/shareable-entities";
import { EntityComments } from "../interactions/entity-comments";
import { EntityReactions } from "../interactions/entity-reactions";
import { InteractionFooter } from "../interactions/interaction-footer";
import { useEntityInteractionHandlers } from "../interactions/use-entity-interaction-handlers";
import { useBatchSignedMedia } from "@/lib/hooks/use-signed-media";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { createThumbnail, processImage } from "@/lib/utils/image";
import { hubToast } from "../hub-toasts";
import type { DraftMomentPhoto } from "../use-hub-store";
import {
  SWAP_PROMPTS,
  RANDOM_SWAP_PROMPTS,
  type SwapPrompt,
} from "@somethings-ui/core/src/swap-prompts";
import { useLightbox } from "../shared/use-lightbox";
import { Lightbox } from "../shared/lightbox";
import { SwapPhotoCard } from "./swap-photo-card";
import { usePostHogEvent } from "@/lib/posthog";
import { ScheduledEntityActions } from "../scheduled-entity-actions";

interface ViewSwapViewProps {
  entity: ShareableEntity;
  menteeId: string;
  mentorId: string;
  currentIndex?: number;
  totalEntities?: number;
  isLoadingNextPage?: boolean;
  activeTab?: "new" | "all";
  onPrevious?: () => void;
  onNext?: () => void;
  onNavigateToList?: () => void;
}

export function ViewSwapView({
  entity,
  menteeId,
  mentorId,
  currentIndex = 1,
  totalEntities = 1,
  isLoadingNextPage = false,
  activeTab,
  onPrevious,
  onNext,
  onNavigateToList,
}: ViewSwapViewProps) {
  const {
    user,
    isRead,
    isFromCurrentUser,
    comments,
    reactions,
    isInteractionPending,
    handleReaction,
    handleComment,
    toggleReadStatus,
  } = useEntityInteractionHandlers({
    entity,
    menteeId,
    entityType: "swap",
    activeTab,
    onNavigateToList,
  });

  const swap = entity.swap;

  const prompt = useMemo(
    () =>
      SWAP_PROMPTS.find((p: SwapPrompt) => p.id === swap?.promptId) ??
      RANDOM_SWAP_PROMPTS.find((p: SwapPrompt) => p.id === swap?.promptId),
    [swap?.promptId],
  );

  const isPendingTeen = swap?.status === "pending_teen";
  const isPendingMentor = swap?.status === "pending_mentor";
  const isCompleted = swap?.status === "completed";

  const { dispatchEvent } = usePostHogEvent("mentor_hub_swap_completed", {
    onMount: false,
  });

  // Show the card that needs upload in front
  const [mentorInFront, setMentorInFront] = useState(!isPendingTeen);
  const [photo, setPhoto] = useState<DraftMomentPhoto | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Reset state when switching to a different entity
  useEffect(() => {
    setPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });
    setMentorInFront(!isPendingTeen);
  }, [entity.id, isPendingTeen]);

  // Determine mentor and teen info based on menteeId
  const teenInfo =
    entity.owner.id === menteeId ? entity.owner : entity.shares[0]?.sharedWith;
  const mentorInfo =
    entity.owner.id === menteeId ? entity.shares[0]?.sharedWith : entity.owner;

  // Only the actual mentor of this mentorship can upload — admins see read-only
  const userIsMentor = user?.id === mentorId;
  const needsUserUpload = isPendingMentor && userIsMentor;

  const presignedUrlMutation = useSwapPresignedUrl();
  const addSwapImageMutation = useAddSwapImage();

  const imageUrls = useMemo(() => {
    const urls: string[] = [];
    if (swap?.mentorImage?.url) urls.push(swap.mentorImage.url);
    if (swap?.menteeImage?.url) urls.push(swap.menteeImage.url);
    return urls;
  }, [swap?.mentorImage, swap?.menteeImage]);

  const signedUrls = useBatchSignedMedia(imageUrls);

  const mentorImageSigned = useMemo(() => {
    if (!swap?.mentorImage) return null;
    return {
      ...swap.mentorImage,
      signedUrl: signedUrls[0]?.url ?? swap.mentorImage.url,
    };
  }, [swap?.mentorImage, signedUrls]);

  const teenImageSigned = useMemo(() => {
    if (!swap?.menteeImage) return null;
    const idx = swap?.mentorImage ? 1 : 0;
    return {
      ...swap.menteeImage,
      signedUrl: signedUrls[idx]?.url ?? swap.menteeImage.url,
    };
  }, [swap?.menteeImage, swap?.mentorImage, signedUrls]);

  const handleRemovePhoto = useCallback(() => {
    setPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });
  }, []);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    setIsConverting(true);
    try {
      const processedFile = await processImage(file);

      const newPhoto: DraftMomentPhoto = {
        id: crypto.randomUUID(),
        file: processedFile,
        preview: URL.createObjectURL(processedFile),
      };

      setPhoto((prev) => {
        if (prev) URL.revokeObjectURL(prev.preview);
        return newPhoto;
      });
    } catch (error) {
      console.error("Failed to process photo:", error);
    } finally {
      setIsConverting(false);
    }
  }, []);

  const handleUpload = async () => {
    if (!photo) return;

    try {
      setIsUploading(true);

      // 1. Get presigned URL and create thumbnail
      const [presignedData, thumbnail] = await Promise.all([
        presignedUrlMutation.mutateAsync({
          filename: photo.file.name,
          contentType: photo.file.type,
        }),
        createThumbnail(photo.file),
      ]);

      // 2. Upload both images to S3
      await Promise.all([
        axios.put(presignedData.highQuality.url, photo.file, {
          headers: { "Content-Type": photo.file.type },
        }),
        axios.put(presignedData.thumbnail.url, thumbnail, {
          headers: { "Content-Type": thumbnail.type },
        }),
      ]);

      // 3. Add image to swap (use entity.id, not swap.id)
      await addSwapImageMutation.mutateAsync({
        swapId: entity.id,
        params: {
          key: presignedData.highQuality.key,
          url: presignedData.highQuality.url.split("?")[0],
          thumbnailKey: presignedData.thumbnail.key,
          thumbnailUrl: presignedData.thumbnail.url.split("?")[0],
        },
      });

      // Fire PostHog event when mentor completes the swap (responding to teen-initiated swap)
      if (isPendingMentor) {
        const responseTimeHours = DateTime.now().diff(
          DateTime.fromISO(entity.created_at),
          "hours",
        ).hours;
        dispatchEvent("mentor_hub_swap_completed", {
          response_time_hours: Math.round(responseTimeHours * 100) / 100,
          prompt_id: swap?.promptId,
        });
      }

      hubToast.sent("swap");
      if (photo) URL.revokeObjectURL(photo.preview);
      setPhoto(null);

      // Navigate back to list view after successful upload
      if (onNavigateToList) {
        onNavigateToList();
      }
    } catch (error) {
      console.error("Failed to upload photo:", error);
      hubToast.error("Failed to upload photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const isScheduled = entity.status === "scheduled";
  // Swaps are collaborative — both participants can react/comment on completed swaps
  // regardless of who initiated them
  const canInteract = !isScheduled && isCompleted && userIsMentor;

  const headerTitle = isPendingTeen || isPendingMentor
    ? "Swap in progress"
    : isFromCurrentUser
      ? "Swap from you"
      : `Swap from ${entity.owner.name}`;

  const createdDate = DateTime.fromJSDate(new Date(entity.created_at));
  const day = createdDate.day;
  const dayWithSuffix =
    day === 1 || day === 21 || day === 31
      ? `${day}st`
      : day === 2 || day === 22
        ? `${day}nd`
        : day === 3 || day === 23
          ? `${day}rd`
          : `${day}th`;
  const formattedCreatedDate = `${createdDate.toFormat("MMM")} ${dayWithSuffix} at ${createdDate.toFormat("h:mm a")}`;

  const swapPreviewThumbnail = mentorImageSigned ? (
    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md">
      <img
        src={mentorImageSigned.signedUrl}
        alt="Swap thumbnail"
        className="h-full w-full object-cover"
      />
    </div>
  ) : null;

  // Lightbox
  const lightboxImages = useMemo(() => {
    const images: { url: string; ownerName: string; avatarUrl?: string }[] = [];
    if (mentorImageSigned) {
      images.push({
        url: mentorImageSigned.signedUrl,
        ownerName: mentorInfo?.name ?? "Mentor",
        avatarUrl: mentorInfo?.profile_picture_url ?? undefined,
      });
    }
    if (teenImageSigned) {
      images.push({
        url: teenImageSigned.signedUrl,
        ownerName: teenInfo?.name ?? "Teen",
        avatarUrl: teenInfo?.profile_picture_url ?? undefined,
      });
    }
    return images;
  }, [mentorImageSigned, teenImageSigned, mentorInfo, teenInfo]);

  const lightbox = useLightbox(lightboxImages.length);

  const openLightbox = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    lightbox.open(index);
  };

  if (entity.metadata?.takedown) {
    return (
      <EntityRemovedPanel
        title="Photo Swap"
        alertId={entity.metadata?.takedown_alert_id as string | undefined}
        pagination={{
          current: currentIndex,
          total: totalEntities,
          isLoadingNextPage,
          onPrevious,
          onNext,
        }}
      />
    );
  }

  return (
    <div className="flex h-full flex-col bg-white">
      <EntityHeader
        title={headerTitle}
        pagination={{
          current: currentIndex,
          total: totalEntities,
          isLoadingNextPage,
          onPrevious,
          onNext,
        }}
        owner={
          isCompleted
            ? {
                name: entity.owner.name,
                avatarUrl: entity.owner.profile_picture_url ?? undefined,
              }
            : undefined
        }
        createdAt={isCompleted ? entity.created_at : undefined}
        subtitle={
          isScheduled && entity.scheduledAt
            ? `Scheduled for ${DateTime.fromISO(entity.scheduledAt).toFormat("MMM d, h:mm a")}`
            : undefined
        }
        actions={isScheduled ? <ScheduledEntityActions entity={entity} /> : undefined}
      />

      {(isPendingTeen || isPendingMentor) && (
        <div className="px-6 pb-2">
          <p className="text-sm text-gray-500">
            Created {formattedCreatedDate}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto pt-4">
        {/* Swap Container */}
        <div className="relative mx-auto mb-6 mt-6 h-[600px] w-[480px]">
          {/* Back card - clickable to bring to front */}
          <SwapPhotoCard
            avatarUrl={
              mentorInFront
                ? teenInfo?.profile_picture_url ?? undefined
                : mentorInfo?.profile_picture_url ?? undefined
            }
            avatarName={mentorInFront ? teenInfo?.name ?? "Teen" : mentorInfo?.name ?? "Mentor"}
            imageUrl={
              mentorInFront
                ? teenImageSigned?.signedUrl
                : mentorImageSigned?.signedUrl
            }
            imageAlt={mentorInFront ? "Teen's swap photo" : "Mentor's swap photo"}
            onExpand={
              (mentorInFront ? teenImageSigned : mentorImageSigned)
                ? (e) => openLightbox(mentorInFront ? 1 : 0, e)
                : undefined
            }
            onClick={teenImageSigned ? () => setMentorInFront(!mentorInFront) : undefined}
            className="absolute left-0 top-4 h-[520px] w-[400px] overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300"
            style={{
              transform: "rotate(-8deg)",
              zIndex: 1,
              cursor: teenImageSigned ? "pointer" : "default",
            }}
          />

          {/* Front card */}
          <SwapPhotoCard
            avatarUrl={
              mentorInFront
                ? mentorInfo?.profile_picture_url ?? undefined
                : teenInfo?.profile_picture_url ?? undefined
            }
            avatarName={mentorInFront ? mentorInfo?.name ?? "Mentor" : teenInfo?.name ?? "Teen"}
            imageUrl={
              mentorInFront
                ? mentorImageSigned?.signedUrl
                : teenImageSigned?.signedUrl
            }
            imageAlt={mentorInFront ? "Mentor's swap photo" : "Teen's swap photo"}
            photo={photo}
            needsUpload={mentorInFront && needsUserUpload}
            isConverting={isConverting}
            onFileSelect={handleFileSelect}
            onRemovePhoto={handleRemovePhoto}
            onExpand={
              (mentorInFront ? mentorImageSigned : teenImageSigned)
                ? (e) => openLightbox(mentorInFront ? 0 : 1, e)
                : undefined
            }
            className="absolute right-0 top-0 h-[520px] w-[400px] overflow-hidden rounded-2xl bg-white shadow-xl"
            style={{
              transform: "rotate(4deg)",
              zIndex: 2,
            }}
          />
        </div>

        {/* Prompt Title & Description */}
        <div className="mx-6 mb-4 text-center">
          <h3 className="text-xl font-bold text-gray-900">
            {swap?.promptId === "custom"
              ? (swap.customTitle ?? "Swap")
              : (prompt?.title ?? "Swap")}
          </h3>
          {swap?.promptId !== "custom" && prompt?.subtitle && (
            <p className="mt-1 text-sm text-gray-500">{prompt.subtitle}</p>
          )}
        </div>

        {/* Waiting message for pending swaps */}
        {isPendingTeen && (
          <div className="mx-6 mb-4 text-center">
            <p className="text-sm text-amber-600">
              Waiting on {teenInfo?.name ?? "your teen"} to complete the swap
            </p>
          </div>
        )}

        {/* Show reactions if any */}
        {reactions.length > 0 && (
          <EntityReactions reactions={reactions} className="mx-6 mt-4" />
        )}

        {/* Show comments if any */}
        {comments.length > 0 && (
          <div className="mx-6 mt-4">
            <EntityComments comments={comments} />
          </div>
        )}
      </div>

      {/* Upload button - show when user needs to upload their photo */}
      {!isScheduled && needsUserUpload && (
        <div className="border-t border-gray-200 bg-white p-4">
          <button
            type="button"
            onClick={handleUpload}
            disabled={!photo || isUploading}
            className={`w-full rounded-full py-4 text-base font-semibold transition-colors ${
              photo && !isUploading
                ? "bg-black text-white hover:bg-gray-800"
                : "cursor-not-allowed bg-gray-200 text-gray-400"
            }`}
          >
            {isUploading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </span>
            ) : (
              "Send"
            )}
          </button>
        </div>
      )}

      {/* Action bar - only show for completed swaps to actual participants */}
      {canInteract && (
        <InteractionFooter
          ownerName={entity.owner.name}
          isRead={isRead}
          isPending={isInteractionPending}
          previewThumbnail={swapPreviewThumbnail}
          onReaction={handleReaction}
          onComment={handleComment}
          onMarkAsRead={toggleReadStatus}
        />
      )}

      {lightbox.isOpen && lightboxImages[lightbox.currentIndex] && (
        <Lightbox
          images={lightboxImages.map((img) => ({
            url: img.url,
            alt: `Swap photo from ${img.ownerName}`,
          }))}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.close}
          onPrev={lightbox.prev}
          onNext={lightbox.next}
          header={
            <>
              <UserAvatar
                imageUrl={lightboxImages[lightbox.currentIndex].avatarUrl}
                name={lightboxImages[lightbox.currentIndex].ownerName}
                size={32}
                showBorder
              />
              <span className="text-sm font-medium">
                {lightboxImages[lightbox.currentIndex].ownerName}
              </span>
            </>
          }
        />
      )}
    </div>
  );
}
